import { useEffect, useState } from 'react';
import cn from 'clsx';
import useSwr from 'swr';

import toast from 'react-hot-toast';
import WrappedPlanSubscribeCheckoutForm from '@components/modules/Account/PlanSubscribeCheckoutForm';
import './AccountPlanPage.css';

import { formatCurrency } from '@utils/Currency';
import { useAuth } from '@hooks/Auth';

import { Tier } from '@sdk/API';
import { getSubscribedPackage } from '@sdk/managers/PackageManager';
import { format } from 'date-fns';

interface PlanCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  price: number;
  description?: string | JSX.Element;
  active?: boolean;
  disabled?: boolean;
}

const PlanCard: React.FC<PlanCardProps> = ({
  title,
  price,
  disabled,
  description,
  className,
  active,
  onClick,
  ...props
}) => {
  const rootCn = cn(
    'bg-black p-4 rounded-lg border-2',
    {
      'shadow-r-gray-dark-sm': !active,
      'hover:bg-blue hover:bg-opacity-60 hover:border-magenta': !disabled,
      'border-blue bg-blue shadow-r-magenta-sm': active,
      'cursor-not-allowed bg-gray-darkest border-opacity-50 text-gray-brand':
        !active && disabled,
    },
    className
  );
  const imageBackground = cn(
    {
      mortal: !active && title === 'Mortal',
      'mortal-active': active && title === 'Mortal',
      hero: !active && title === 'Hero',
      'hero-active': active && title === 'Hero',
      superhero: !active && title === 'Superhero',
      'superhero-active': active && title === 'Superhero',
      immortal: !active && title === 'Immortal',
      'immortal-active': active && title === 'Immortal',
    },
    'md:h-60 md:w-60',
    className
  );
  return (
    <div
      role="button"
      aria-disabled={disabled}
      onClick={disabled ? undefined : onClick}
      className={rootCn}
      {...props}
    >
      <div className="mb-2 md:flex">
        <div className="mb-2 md:mb-0 md:mr-5">
          <img className={imageBackground} alt={`plan-${title}`}></img>
        </div>
        <div className={'flex-grow md:flex md:justify-between'}>
          <div className="mb-3 md:mb-0">
            <h3 className="text-lg">{title}</h3>
            {description && (
              <div className="whitespace-pre-line">
                {description}
                {title === 'Immortal' && (
                  <div className="mt-2">
                    For More Details:<br></br>
                    <a
                      className="hover:underline"
                      href="https://megax.world/faqs#contact-form"
                      rel="noreferrer"
                      target="_blank"
                    >
                      Drop Us a Message
                    </a>
                    <br></br>
                    Or<br></br>
                    <a
                      className="hover:underline"
                      href="mailto: contact@megax.world"
                    >
                      Email us: contact@megax.world
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="text-lg">
            {title !== 'Immortal' ? formatCurrency(price) : '-'}
            /YEAR
          </div>
        </div>
      </div>
    </div>
  );
};

const TIER_MAP = {
  [Tier.FREE]: 'MORTAL',
  [Tier.ENTERPRISE]: 'SUPERHERO',
  [Tier.CONSULTATIVE]: 'IMMORTAL',
};

const PlanPage: React.FC = () => {
  const { dbUser, refetch, loading, company } = useAuth();
  const [active, setActive] = useState<string>(dbUser?.tier || Tier.FREE);

  const { data } = useSwr(
    !loading && dbUser?.id ? ['/admin/approval', dbUser.id] : null,
    async () => {
      if (!dbUser?.id) {
        throw Error('User no logon.');
      }

      // TODO: Backend fix
      const res: any = await getSubscribedPackage(dbUser?.id);
      return res || [];
    },
    {
      revalidateOnFocus: true,
      onError: (err, key) => {
        console.log('Failed to fetch subscribed plan', { key }, err);
        toast.error(err?.message || err?.errors?.[0]?.message);
      },
    }
  );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setActive(dbUser?.tier || Tier.FREE);
  }, [dbUser]);

  return (
    <div className="account-plan-background">
      <div className="container mx-auto">
        <div className="p-4 mt-4 mb-8 text-black bg-yellow">
          <div className="mb-4">
            <h1 className="my-2 text-3xl">Manage your plan</h1>
            <p>Designed For Individuals To Enterprises</p>
          </div>

          <div className="mb-4 text-xl">
            <div>
              Your current plan{' '}
              <span className="text-magenta">
                {TIER_MAP[dbUser?.tier || Tier.FREE]}
              </span>
            </div>
            {data?.current_period_end && (
              <div className="text-sm text-bold">
                Expiring{' '}
                {format(new Date(data.current_period_end * 1000), 'dd MMM yy')}
              </div>
            )}
          </div>
        </div>

        <h2 className="mb-4 text-xl">Change your plan</h2>
        <div className="space-y-4">
          <PlanCard
            title="Mortal"
            active={active === Tier.FREE}
            disabled={
              dbUser?.tier === Tier.ENTERPRISE ||
              dbUser?.tier === Tier.CONSULTATIVE
            }
            price={0}
            description={`• Create poll with up to 4 poll options
            • 24 hours poll expiry
            • Single-question poll`}
            onClick={() => setActive(Tier.FREE)}
          />
          {/* <PlanCard
            title="Hero"
            active={active === Tier.STANDARD}
            disabled={
              dbUser?.tier === Tier.ENTERPRISE ||
              dbUser?.tier === Tier.CONSULTATIVE
            }
            price={99}
            description={`• Create poll with up to 10 poll options
            • 3% Pledge admin charges
            • Option to Pledge winning poll
            • Single-question poll
            • Flexible poll duration
            • Statistics report`}
            onClick={() => setActive(Tier.STANDARD)}
          /> */}
          <PlanCard
            title="Superhero"
            active={active === Tier.ENTERPRISE}
            price={800}
            disabled={dbUser?.tier !== Tier.ENTERPRISE && !!company}
            description={`${
              company ? `Disabled, you are already in a company\n` : ''
            }• Create up to 1000 polls annually
            • Create poll with up to 10 poll options
            • 2% Pledge admin charges
            • Option to Pledge winning poll
            • Multi-questions poll
            • Flexible poll duration
            • Statistics report`}
            onClick={() => setActive(Tier.ENTERPRISE)}
          />
          <PlanCard
            title="Immortal"
            disabled
            active={active === Tier.CONSULTATIVE}
            price={-1}
            description={`• Unlimited poll creation
            • Create poll with up to 20 poll options
            • 1.5% MegaX World pledge charges*
            • Option to Pledge winning poll
            • Multi-questions poll
            • Flexible poll duration
            • Statistics report`}
          />
        </div>

        {active !== 'MORTAL' && active !== dbUser?.tier && (
          <div className="px-4 py-4 mt-8 bg-black rounded-lg">
            <h2 className="pb-6">Payment information</h2>
            <WrappedPlanSubscribeCheckoutForm plan={active} />
          </div>
        )}
        {/* <div className="py-8">
          <ButtonMighty
            disabled={!isChanged}
            color={isChanged ? '#4400FF' : '#979797'}
            text="Update"
            borderColor={isChanged ? '#ff1dfc' : '#DDDDDD'}
            style={{
              cursor: isChanged ? 'pointer' : 'not-allowed',
            }}
            margin="0"
            textColor="#fff"
          />
        </div> */}
      </div>
    </div>
  );
};

export default PlanPage;
